import React, { useEffect, useMemo, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { createRoot } from 'react-dom/client';
import 'maplibre-gl/dist/maplibre-gl.css';
import { BASEMAPS } from 'components/Tiles/basemaps';
import {
  BENTHIC_TILESETS,
  DEFAULT_BENTHIC_CLASS_COLORS,
} from 'components/Tiles/BenthicTileLayer';
import {
  REEF_EXTENT_TILESETS,
  DEFAULT_REEF_EXTENT_CLASS_COLORS,
} from 'components/Tiles/ReefExtentTileLayer';
import {
  ALL_BLEACHING_YEARS,
  buildBleachingSeverityColorExpression,
  buildBleachingYearFilter,
} from 'components/Bleaching/bleachingSeverity';
import ColonyPopupContent from 'components/ColonyPopup/ColonyPopupContent';
import CortadHistory from 'components/ColonyPopup/CortadHistory';
import {
  COLONIES_SOURCE,
  CLUSTERS_LAYER,
  UNCLUSTERED_MARKERS_LAYER,
  SPIDER_POINTS_LAYER,
  addLeafletClusterSourcesAndLayers,
  coloniesToGeoJson,
  createLeafletClusterController,
  ensureLeafletMarkerImages,
} from './leafletMarkerCluster';
import {
  EXPEDITION_TRACK_ROUTE_SOURCE,
  EXPEDITION_TRACK_STOPS_SOURCE,
  addExpeditionTrackSourcesAndLayers,
  applyExpeditionTrackData,
  applyExpeditionTrackVisibility,
  ensureExpeditionArrowImage,
  getExpeditionTrackBounds,
} from './expeditionTrackLayers';

import { CORTAD_MAX_ZOOM, CORTAD_MIN_ZOOM, cortadTileUrl } from 'utils/cortadLayers';

const BENTHIC_SOURCE_LAYER = process.env.REACT_APP_BENTHIC_VECTOR_LAYER || 'benthic';
const BENTHIC_MIN_ZOOM = Number(process.env.REACT_APP_BENTHIC_MIN_ZOOM || 5);
const CAPTIONS_SOURCE = 'map-captions';
const CAPTIONS_LAYER = 'map-captions';
const DEFAULT_CAPTIONS_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';
const OCEAN_CAPTIONS_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}';

const toAbsoluteTileUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  if (url.startsWith('/')) return `${window.location.origin}${url}`;
  return `${window.location.origin}/${url}`;
};

const sourceIdFor = (tilesetId) => `benthic-${tilesetId}`;
const fillLayerIdFor = (tilesetId) => `benthic-${tilesetId}-fill`;
const lineLayerIdFor = (tilesetId) => `benthic-${tilesetId}-line`;

const reefSourceIdFor = (tilesetId) => `reef-extent-${tilesetId}`;
const reefFillLayerIdFor = (tilesetId) => `reef-extent-${tilesetId}-fill`;
const reefLineLayerIdFor = (tilesetId) => `reef-extent-${tilesetId}-line`;

const isCortadTileError = (error) => {
  const url = error?.url || error?.message || '';
  return typeof url === 'string' && url.includes('/cortad-tiles/');
};

const CORTAD_SOURCE = 'cortad-raster';
const CORTAD_LAYER = 'cortad-raster-layer';
const BLEACHING_SOURCE = 'bleaching-grid';
const BLEACHING_LAYER = 'bleaching-grid-fill';
const BLEACHING_OBS_SOURCE = 'bleaching-observations';
const BLEACHING_OBS_LAYER = 'bleaching-observations-circles';
const BLEACHING_SOURCE_LAYER = process.env.REACT_APP_BLEACHING_VECTOR_LAYER || 'bleaching';

const captionsUrlFor = (basemap) => (basemap === 'ocean' ? OCEAN_CAPTIONS_URL : DEFAULT_CAPTIONS_URL);

const isBenthicTileError = (error) => {
  const url = error?.url || error?.message || '';
  return typeof url === 'string' && url.includes('/benthic-tiles/');
};

const isReefExtentTileError = (error) => {
  const url = error?.url || error?.message || '';
  return typeof url === 'string' && url.includes('/reef-extent-tiles/');
};

const isBleachingTileError = (error) => {
  const url = error?.url || error?.message || '';
  return typeof url === 'string' && url.includes('/bleaching-tiles/');
};

const buildColorMatchExpression = (classSettings = {}) => {
  const entries = Object.entries(DEFAULT_BENTHIC_CLASS_COLORS);
  const match = ['match', ['get', 'class']];
  for (const [className, defaultColor] of entries) {
    const c = classSettings[className]?.color || defaultColor;
    match.push(className, c);
  }
  match.push('#14b8a6');
  return match;
};

const buildVisibleClassFilter = (classSettings = {}) => {
  const visible = Object.entries(DEFAULT_BENTHIC_CLASS_COLORS)
    .filter(([className]) => classSettings[className]?.visible !== false)
    .map(([className]) => className);
  return ['in', ['get', 'class'], ['literal', visible]];
};

const buildReefExtentColorMatchExpression = (classSettings = {}) => {
  const entries = Object.entries(DEFAULT_REEF_EXTENT_CLASS_COLORS);
  const match = ['match', ['get', 'class']];
  for (const [className, defaultColor] of entries) {
    const c = classSettings[className]?.color || defaultColor;
    match.push(className, c);
  }
  match.push('#34d399');
  return match;
};

const buildVisibleReefExtentFilter = (classSettings = {}) => {
  const visible = Object.entries(DEFAULT_REEF_EXTENT_CLASS_COLORS)
    .filter(([className]) => classSettings[className]?.visible !== false)
    .map(([className]) => className);
  return ['in', ['get', 'class'], ['literal', visible]];
};

const REEF_EXTENT_SOURCE_LAYER =
  process.env.REACT_APP_REEF_EXTENT_VECTOR_LAYER || 'reef_extent';

const getMaplibreActiveTilesetIds = (map, tilesets) => {
  const getCenter = () => {
    const c = map.getCenter();
    return {
      lat: c.lat,
      lng: c.lng,
      distanceTo: (other) => {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(other.lat - c.lat);
        const dLng = toRad(other.lng - c.lng);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(c.lat)) * Math.cos(toRad(other.lat)) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(a));
      },
    };
  };

  const z = map.getZoom();
  const bounds = map.getBounds();
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const pad = 0.06;
  const swp = { lat: sw.lat - (ne.lat - sw.lat) * pad, lng: sw.lng - (ne.lng - sw.lng) * pad };
  const nep = { lat: ne.lat + (ne.lat - sw.lat) * pad, lng: ne.lng + (ne.lng - sw.lng) * pad };

  const hits = tilesets.filter((t) => t.bounds).filter((t) => {
    const [[s, w], [n, e]] = t.bounds;
    const intersects =
      e >= swp.lng && w <= nep.lng && n >= swp.lat && s <= nep.lat;
    return intersects;
  });

  if (hits.length === 0) return [];
  const strictZoom = Number(process.env.REACT_APP_BENTHIC_ZOOM_STRICT_REGION || 5);
  if (z < strictZoom && hits.length > 1) {
    const center = getCenter();
    const best = hits.reduce((acc, t) => {
      const [[s, w], [n, e]] = t.bounds;
      const c = { lat: (s + n) / 2, lng: (w + e) / 2 };
      const d = center.distanceTo(c);
      if (!acc || d < acc.d) return { id: t.id, d };
      return acc;
    }, null);
    return best ? [best.id] : [];
  }
  return hits.map((t) => t.id);
};

const getMaplibreActiveBenthicTilesetIds = (map) => getMaplibreActiveTilesetIds(map, BENTHIC_TILESETS);
const getMaplibreActiveReefExtentTilesetIds = (map) =>
  getMaplibreActiveTilesetIds(map, REEF_EXTENT_TILESETS);

export default function CustomerMapLibreMap({
  basemap = 'imagery',
  captionsVisible = true,
  benthicVisible = true,
  benthicClasses = {},
  reefExtentVisible = false,
  reefExtentClasses = {},
  bleachingVisible = false,
  bleachingYear = ALL_BLEACHING_YEARS,
  bleachingObservationsGeoJson = null,
  onBleachingObservationClick,
  colonies = [],
  focusTarget = null,
  expeditionTrackGeoJson = null,
  expeditionTrackVisible = false,
  expeditionTrackFitBounds = false,
  colonyFitBounds = true,
  cortadVisible = false,
  cortadVariable = 'tsa_dhw',
  cortadMonth = '2016-08',
  onCortadStatusChange,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const clusterControllerRef = useRef(null);
  const basemapRef = useRef(basemap);
  const captionsVisibleRef = useRef(captionsVisible);
  const bleachingVisibleRef = useRef(bleachingVisible);
  const bleachingYearRef = useRef(bleachingYear);
  const onBleachingClickRef = useRef(onBleachingObservationClick);
  const bleachingObsRef = useRef(bleachingObservationsGeoJson);
  const expeditionTrackRef = useRef(expeditionTrackGeoJson);
  const expeditionTrackVisibleRef = useRef(expeditionTrackVisible);
  const cortadVisibleRef = useRef(cortadVisible);
  const cortadVariableRef = useRef(cortadVariable);
  const cortadMonthRef = useRef(cortadMonth);
  const cortadTilesRef = useRef('');
  const cortadPendingClearRef = useRef(false);
  const cortadVariableAppliedRef = useRef(cortadVariable);
  const cortadStatusRef = useRef({ loading: false, zoomTooLow: false });
  const cortadLoadTimerRef = useRef(null);
  const cortadSawUnloadRef = useRef(true);
  const onCortadStatusChangeRef = useRef(onCortadStatusChange);
  const focusHandledKeyRef = useRef(null);

  const style = useMemo(() => {
    const bm = BASEMAPS[basemap] || BASEMAPS.imagery;

    // MapLibre expects {x}/{y}/{z}; some of our providers use {s} and/or {r}.
    // Keep the existing URLs; MapLibre supports {x}/{y}/{z} but not {s}.
    // For providers with {s}, we replace with 'a' as a simple default.
    const rasterUrl = bm.url.replace('{s}', 'a').replace('{r}', '');

    const sources = {
      basemap: {
        type: 'raster',
        tiles: [rasterUrl],
        tileSize: 256,
      },
      [CORTAD_SOURCE]: {
        type: 'raster',
        tiles: [toAbsoluteTileUrl(cortadTileUrl('tsa_dhw', '2016-08'))],
        tileSize: 256,
        minzoom: CORTAD_MIN_ZOOM,
        maxzoom: CORTAD_MAX_ZOOM,
        attribution: 'NOAA CoRTAD v6',
      },
      [CAPTIONS_SOURCE]: {
        type: 'raster',
        tiles: [captionsUrlFor(basemap)],
        tileSize: 256,
      },
    };

    const layers = [
      {
        id: 'basemap',
        type: 'raster',
        source: 'basemap',
      },
      {
        id: CORTAD_LAYER,
        type: 'raster',
        source: CORTAD_SOURCE,
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.78, 'raster-fade-duration': 0 },
        minzoom: CORTAD_MIN_ZOOM,
        maxzoom: CORTAD_MAX_ZOOM + 4,
      },
    ];

    for (const t of REEF_EXTENT_TILESETS) {
      sources[reefSourceIdFor(t.id)] = {
        type: 'vector',
        tiles: [toAbsoluteTileUrl(t.url)],
        minzoom: BENTHIC_MIN_ZOOM,
        maxzoom: Number(process.env.REACT_APP_REEF_EXTENT_MAX_NATIVE_ZOOM || 16),
      };
      layers.push(
        {
          id: reefFillLayerIdFor(t.id),
          type: 'fill',
          source: reefSourceIdFor(t.id),
          'source-layer': REEF_EXTENT_SOURCE_LAYER,
          layout: { visibility: 'none' },
          minzoom: BENTHIC_MIN_ZOOM,
          paint: {
            'fill-color': buildReefExtentColorMatchExpression({}),
            'fill-opacity': 0.55,
          },
          filter: buildVisibleReefExtentFilter({}),
        },
        {
          id: reefLineLayerIdFor(t.id),
          type: 'line',
          source: reefSourceIdFor(t.id),
          'source-layer': REEF_EXTENT_SOURCE_LAYER,
          layout: { visibility: 'none' },
          minzoom: BENTHIC_MIN_ZOOM,
          paint: {
            'line-color': buildReefExtentColorMatchExpression({}),
            'line-opacity': 0.35,
            'line-width': 0.5,
          },
          filter: buildVisibleReefExtentFilter({}),
        },
      );
    }

    for (const t of BENTHIC_TILESETS) {
      sources[sourceIdFor(t.id)] = {
        type: 'vector',
        tiles: [toAbsoluteTileUrl(t.url)],
        minzoom: BENTHIC_MIN_ZOOM,
        maxzoom: Number(process.env.REACT_APP_BENTHIC_MAX_NATIVE_ZOOM || 16),
      };
      layers.push(
        {
          id: fillLayerIdFor(t.id),
          type: 'fill',
          source: sourceIdFor(t.id),
          'source-layer': BENTHIC_SOURCE_LAYER,
          layout: { visibility: 'none' },
          minzoom: BENTHIC_MIN_ZOOM,
          paint: {
            'fill-color': buildColorMatchExpression({}),
            'fill-opacity': 0.88,
          },
          filter: buildVisibleClassFilter({}),
        },
        {
          id: lineLayerIdFor(t.id),
          type: 'line',
          source: sourceIdFor(t.id),
          'source-layer': BENTHIC_SOURCE_LAYER,
          layout: { visibility: 'none' },
          minzoom: BENTHIC_MIN_ZOOM,
          paint: {
            'line-color': buildColorMatchExpression({}),
            'line-opacity': 0.5,
            'line-width': 0.6,
          },
          filter: buildVisibleClassFilter({}),
        },
      );
    }

    sources[BLEACHING_SOURCE] = {
      type: 'vector',
      tiles: [toAbsoluteTileUrl('/api/public/bleaching-tiles/{z}/{x}/{y}.pbf')],
      minzoom: 2,
      maxzoom: 14,
    };
    layers.push({
      id: BLEACHING_LAYER,
      type: 'fill',
      source: BLEACHING_SOURCE,
      'source-layer': BLEACHING_SOURCE_LAYER,
      layout: { visibility: 'none' },
      minzoom: 2,
      paint: {
        'fill-color': buildBleachingSeverityColorExpression(),
        'fill-opacity': 0.82,
        'fill-outline-color': 'rgba(0,0,0,0.12)',
      },
    });

    sources[BLEACHING_OBS_SOURCE] = {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    };
    layers.push({
      id: BLEACHING_OBS_LAYER,
      type: 'circle',
      source: BLEACHING_OBS_SOURCE,
      layout: { visibility: 'none' },
      minzoom: 2,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 2, 6, 5, 10, 7, 14, 10],
        'circle-color': buildBleachingSeverityColorExpression(),
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#1f2937',
        'circle-opacity': 0.95,
      },
    });

    layers.push({
      id: CAPTIONS_LAYER,
      type: 'raster',
      source: CAPTIONS_SOURCE,
      layout: { visibility: 'none' },
    });

    addLeafletClusterSourcesAndLayers(sources, layers);
    addExpeditionTrackSourcesAndLayers(sources, layers);

    return {
      version: 8,
      name: 'Customer map',
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources,
      layers,
    };
  }, [basemap]);

  const coloniesRef = useRef(colonies);
  useEffect(() => {
    captionsVisibleRef.current = captionsVisible;
  }, [captionsVisible]);

  useEffect(() => {
    bleachingVisibleRef.current = bleachingVisible;
  }, [bleachingVisible]);

  useEffect(() => {
    bleachingYearRef.current = bleachingYear;
  }, [bleachingYear]);

  useEffect(() => {
    onBleachingClickRef.current = onBleachingObservationClick;
  }, [onBleachingObservationClick]);

  useEffect(() => {
    bleachingObsRef.current = bleachingObservationsGeoJson;
  }, [bleachingObservationsGeoJson]);

  useEffect(() => {
    coloniesRef.current = colonies;
  }, [colonies]);

  useEffect(() => {
    expeditionTrackRef.current = expeditionTrackGeoJson;
  }, [expeditionTrackGeoJson]);

  useEffect(() => {
    expeditionTrackVisibleRef.current = expeditionTrackVisible;
  }, [expeditionTrackVisible]);

  useEffect(() => {
    cortadVisibleRef.current = cortadVisible;
  }, [cortadVisible]);

  useEffect(() => {
    cortadVariableRef.current = cortadVariable;
  }, [cortadVariable]);

  useEffect(() => {
    cortadMonthRef.current = cortadMonth;
  }, [cortadMonth]);

  useEffect(() => {
    onCortadStatusChangeRef.current = onCortadStatusChange;
  }, [onCortadStatusChange]);

  const applyExpeditionTrack = (map) => {
    const routeSource = map.getSource(EXPEDITION_TRACK_ROUTE_SOURCE);
    const stopsSource = map.getSource(EXPEDITION_TRACK_STOPS_SOURCE);
    if (!routeSource?.setData || !stopsSource?.setData) return false;

    applyExpeditionTrackData(map, expeditionTrackRef.current);
    applyExpeditionTrackVisibility(map, expeditionTrackVisibleRef.current);
    return true;
  };

  const scheduleExpeditionTrackUpdate = (map) => {
    if (!map) return;

    const tryApply = () => {
      applyExpeditionTrack(map);
    };

    tryApply();

    const onReady = () => {
      tryApply();
      map.once('idle', tryApply);
    };

    if (map.isStyleLoaded()) {
      onReady();
    } else {
      map.once('load', onReady);
      map.once('style.load', onReady);
    }
  };

  const applyBenthicVisibility = (map) => {
    const activeIds = benthicVisible ? getMaplibreActiveBenthicTilesetIds(map) : [];
    for (const t of BENTHIC_TILESETS) {
      const vis = activeIds.includes(t.id) ? 'visible' : 'none';
      const fillId = fillLayerIdFor(t.id);
      const lineId = lineLayerIdFor(t.id);
      if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', vis);
      if (map.getLayer(lineId)) map.setLayoutProperty(lineId, 'visibility', vis);
    }
  };

  const applyBenthicStyling = (map) => {
    const colorExpr = buildColorMatchExpression(benthicClasses);
    const classFilter = buildVisibleClassFilter(benthicClasses);
    for (const t of BENTHIC_TILESETS) {
      const fillId = fillLayerIdFor(t.id);
      const lineId = lineLayerIdFor(t.id);
      if (map.getLayer(fillId)) {
        map.setPaintProperty(fillId, 'fill-color', colorExpr);
        map.setFilter(fillId, classFilter);
      }
      if (map.getLayer(lineId)) {
        map.setPaintProperty(lineId, 'line-color', colorExpr);
        map.setFilter(lineId, classFilter);
      }
    }
  };

  const applyReefExtentVisibility = (map) => {
    const activeIds = reefExtentVisible ? getMaplibreActiveReefExtentTilesetIds(map) : [];
    for (const t of REEF_EXTENT_TILESETS) {
      const vis = activeIds.includes(t.id) ? 'visible' : 'none';
      const fillId = reefFillLayerIdFor(t.id);
      const lineId = reefLineLayerIdFor(t.id);
      if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', vis);
      if (map.getLayer(lineId)) map.setLayoutProperty(lineId, 'visibility', vis);
    }
  };

  const applyReefExtentStyling = (map) => {
    const colorExpr = buildReefExtentColorMatchExpression(reefExtentClasses);
    const classFilter = buildVisibleReefExtentFilter(reefExtentClasses);
    for (const t of REEF_EXTENT_TILESETS) {
      const fillId = reefFillLayerIdFor(t.id);
      const lineId = reefLineLayerIdFor(t.id);
      if (map.getLayer(fillId)) {
        map.setPaintProperty(fillId, 'fill-color', colorExpr);
        map.setFilter(fillId, classFilter);
      }
      if (map.getLayer(lineId)) {
        map.setPaintProperty(lineId, 'line-color', colorExpr);
        map.setFilter(lineId, classFilter);
      }
    }
  };

  const applyCaptionsVisibility = (map) => {
    if (map.getLayer(CAPTIONS_LAYER)) {
      map.setLayoutProperty(CAPTIONS_LAYER, 'visibility', captionsVisibleRef.current ? 'visible' : 'none');
    }
  };

  const emitCortadStatus = (next) => {
    const prev = cortadStatusRef.current;
    if (prev && prev.loading === next.loading && prev.zoomTooLow === next.zoomTooLow) return;
    cortadStatusRef.current = next;
    onCortadStatusChangeRef.current?.(next);
  };

  const clearCortadLoadTimer = () => {
    if (cortadLoadTimerRef.current) {
      window.clearTimeout(cortadLoadTimerRef.current);
      cortadLoadTimerRef.current = null;
    }
  };

  const restoreCortadOpacity = (map) => {
    cortadPendingClearRef.current = false;
    clearCortadLoadTimer();
    if (!map.getLayer(CORTAD_LAYER) || !cortadVisibleRef.current) return;
    try {
      map.setPaintProperty(CORTAD_LAYER, 'raster-opacity', 0.78);
    } catch (_) {
      // Layer can disappear during a style reload.
    }
  };

  const refreshCortadStatus = (map) => {
    if (!map) return;
    if (!cortadVisibleRef.current) {
      cortadPendingClearRef.current = false;
      clearCortadLoadTimer();
      emitCortadStatus({ loading: false, zoomTooLow: false });
      return;
    }
    const zoomTooLow = (map.getZoom?.() ?? 0) < CORTAD_MIN_ZOOM;
    let sourceLoaded = true;
    try {
      sourceLoaded = map.isSourceLoaded(CORTAD_SOURCE);
    } catch (_) {
      sourceLoaded = false;
    }
    if (!sourceLoaded) {
      cortadSawUnloadRef.current = true;
    }
    const tilesReady = sourceLoaded && cortadSawUnloadRef.current;
    if (zoomTooLow || tilesReady) {
      restoreCortadOpacity(map);
    }
    emitCortadStatus({
      loading: !zoomTooLow && !tilesReady,
      zoomTooLow,
    });
  };

  const applyCortadState = (map, { resetRaster = false } = {}) => {
    const source = map.getSource(CORTAD_SOURCE);
    const month = cortadMonthRef.current;
    const variable = cortadVariableRef.current;
    const next = month && variable ? toAbsoluteTileUrl(cortadTileUrl(variable, month)) : '';
    const tilesChanged = Boolean(source?.setTiles && next && cortadTilesRef.current !== next);
    const wipe = resetRaster || tilesChanged;
    const visible = Boolean(cortadVisibleRef.current && next);
    const zoomTooLow = (map.getZoom?.() ?? 0) < CORTAD_MIN_ZOOM;

    if (wipe && map.getLayer(CORTAD_LAYER)) {
      cortadPendingClearRef.current = true;
      try {
        map.setPaintProperty(CORTAD_LAYER, 'raster-opacity', 0);
      } catch (_) {
        // Ignore if the style is swapping out.
      }
    }
    if (tilesChanged) {
      cortadTilesRef.current = next;
      cortadSawUnloadRef.current = false;
      source.setTiles([next]);
    }
    if (map.getLayer(CORTAD_LAYER)) {
      map.setLayoutProperty(CORTAD_LAYER, 'visibility', visible ? 'visible' : 'none');
    }
    if (!visible) {
      refreshCortadStatus(map);
      return;
    }
    if (wipe && !zoomTooLow) {
      emitCortadStatus({ loading: true, zoomTooLow: false });
      clearCortadLoadTimer();
      cortadLoadTimerRef.current = window.setTimeout(() => {
        cortadSawUnloadRef.current = true;
        restoreCortadOpacity(map);
        refreshCortadStatus(map);
      }, 90000);
      window.setTimeout(() => {
        if (!cortadVisibleRef.current || cortadSawUnloadRef.current) return;
        cortadSawUnloadRef.current = true;
        refreshCortadStatus(map);
      }, 1500);
    }
    window.requestAnimationFrame(() => refreshCortadStatus(map));
  };

  const openCortadPopup = (map, lngLat) => {
    if (!lngLat) return;
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    const node = document.createElement('div');
    node.style.minWidth = '360px';
    node.style.maxWidth = '460px';
    const root = createRoot(node);
    root.render(
      <div style={{ padding: '4px 2px' }}>
        <CortadHistory latitude={lngLat.lat} longitude={lngLat.lng} title="Thermal history" />
      </div>
    );
    const popup = new maplibregl.Popup({
      anchor: 'bottom',
      closeOnClick: false,
      closeOnMove: false,
      focusAfterOpen: false,
      maxWidth: '480px',
    });
    popup.setLngLat(lngLat);
    popup.setDOMContent(node);
    popup.addTo(map);
    popup.on('close', () => {
      try { root.unmount(); } catch (_) { /* ignore */ }
    });
    popupRef.current = popup;
  };

  const applyBleachingVisibility = (map) => {
    const vis = bleachingVisibleRef.current ? 'visible' : 'none';
    if (map.getLayer(BLEACHING_LAYER)) map.setLayoutProperty(BLEACHING_LAYER, 'visibility', vis);
    if (map.getLayer(BLEACHING_OBS_LAYER)) map.setLayoutProperty(BLEACHING_OBS_LAYER, 'visibility', vis);
  };

  const applyBleachingYear = (map, year) => {
    const yearFilter = buildBleachingYearFilter(year);
    if (map.getLayer(BLEACHING_LAYER)) map.setFilter(BLEACHING_LAYER, yearFilter);
    if (map.getLayer(BLEACHING_OBS_LAYER)) map.setFilter(BLEACHING_OBS_LAYER, yearFilter);
  };

  const applyBleachingObservations = (map) => {
    const src = map.getSource(BLEACHING_OBS_SOURCE);
    if (src && bleachingObsRef.current) {
      src.setData(bleachingObsRef.current);
    }
  };

  const applyBleachingState = (map) => {
    applyBleachingVisibility(map);
    applyBleachingYear(map, bleachingYearRef.current);
    applyBleachingObservations(map);
  };

  const applyColoniesData = (map) => {
    const src = map.getSource(COLONIES_SOURCE);
    if (!src || !src.setData) return false;
    src.setData(coloniesToGeoJson(coloniesRef.current));
    return true;
  };

  const openColonyPopup = (map, colony, coordinates) => {
    if (!colony || !Array.isArray(coordinates) || coordinates.length !== 2) return;
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    const node = document.createElement('div');
    node.style.minWidth = '420px';
    node.style.maxWidth = '480px';
    const root = createRoot(node);
    root.render(<ColonyPopupContent colony={colony} />);

    const popup = new maplibregl.Popup({
      anchor: 'bottom',
      closeOnClick: false,
      closeOnMove: false,
      focusAfterOpen: false,
      maxWidth: '480px',
      offset: [0, -4],
    });
    popup.setLngLat(coordinates);
    popup.setDOMContent(node);
    popup.addTo(map);
    popup.on('close', () => {
      try { root.unmount(); } catch (_) { /* ignore */ }
    });
    popupRef.current = popup;
  };

  // init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style,
      center: [0, 0],
      zoom: 2,
      attributionControl: false,
      interactive: true,
    });
    mapRef.current = map;
    clusterControllerRef.current = createLeafletClusterController({
      map,
      getColonies: () => coloniesRef.current,
      openPopup: (colony, coordinates) => openColonyPopup(map, colony, coordinates),
    });

    map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');
    const syncImagesAndHandlers = async () => {
      try {
        await Promise.all([
          ensureLeafletMarkerImages(map),
          ensureExpeditionArrowImage(map),
        ]);
      } catch (_) {
        // Map still works if marker/arrow images fail to load.
      }
      clusterControllerRef.current?.registerInteractions();
      // Style reload recreates sources/layers: re-apply all runtime state.
      applyBenthicStyling(map);
      applyReefExtentStyling(map);
      applyCaptionsVisibility(map);
      applyCortadState(map);
      applyBenthicVisibility(map);
      applyReefExtentVisibility(map);
      applyBleachingState(map);
      applyColoniesData(map);
      scheduleExpeditionTrackUpdate(map);
      clusterControllerRef.current?.clearSpider();
    };
    map.on('load', syncImagesAndHandlers);
    map.on('style.load', syncImagesAndHandlers);
    map.on('click', BLEACHING_OBS_LAYER, (e) => {
      if (!bleachingVisibleRef.current) return;
      const feature = e.features?.[0];
      if (feature?.properties) onBleachingClickRef.current?.(feature.properties);
    });
    map.on('mouseenter', BLEACHING_OBS_LAYER, () => {
      if (bleachingVisibleRef.current) map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', BLEACHING_OBS_LAYER, () => {
      map.getCanvas().style.cursor = '';
    });
    map.on('click', (event) => {
      if (!cortadVisibleRef.current) return;
      const layers = [UNCLUSTERED_MARKERS_LAYER, CLUSTERS_LAYER, SPIDER_POINTS_LAYER, BLEACHING_OBS_LAYER].filter((id) => map.getLayer(id));
      const hits = layers.length ? map.queryRenderedFeatures(event.point, { layers }) : [];
      if (hits.length) return;
      openCortadPopup(map, event.lngLat);
    });
    map.on('error', (event) => {
      if (
        isBenthicTileError(event?.error) ||
        isReefExtentTileError(event?.error) ||
        isBleachingTileError(event?.error) ||
        isCortadTileError(event?.error)
      ) {
        return;
      }
      // Let non-benthic errors keep their normal development visibility.
      // eslint-disable-next-line no-console
      console.error(event?.error || event);
    });
    map.on('zoomstart', () => clusterControllerRef.current?.clearSpider());
    map.on('zoomend', () => clusterControllerRef.current?.renderSpider());
    map.on('moveend', () => clusterControllerRef.current?.renderSpider());
    const onCortadSourceEvent = (event) => {
      if (event?.sourceId && event.sourceId !== CORTAD_SOURCE) return;
      refreshCortadStatus(map);
    };
    map.on('sourcedata', onCortadSourceEvent);
    map.on('dataloading', (event) => {
      if (event?.dataType === 'source' && event.sourceId && event.sourceId !== CORTAD_SOURCE) return;
      if (cortadVisibleRef.current) refreshCortadStatus(map);
    });
    map.on('zoomend', () => refreshCortadStatus(map));

    return () => {
      clearCortadLoadTimer();
      emitCortadStatus({ loading: false, zoomTooLow: false });
      try {
        clusterControllerRef.current?.dispose();
        map.remove();
      } catch (_) {
        // ignore
      }
      clusterControllerRef.current = null;
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild style only when basemap changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (basemapRef.current === basemap) return;
    basemapRef.current = basemap;
    // This triggers `style.load`, where we re-apply images, data, and handlers.
    map.setStyle(style);
  }, [basemap, style]);

  // Benthic visibility + viewport gating.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => applyBenthicVisibility(map);

    if (map.isStyleLoaded()) apply();
    map.on('moveend', apply);
    map.on('zoomend', apply);
    map.on('styledata', apply);
    return () => {
      map.off('moveend', apply);
      map.off('zoomend', apply);
      map.off('styledata', apply);
    };
  }, [benthicVisible]);

  // Reef extent visibility + viewport gating.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => applyReefExtentVisibility(map);

    if (map.isStyleLoaded()) apply();
    map.on('moveend', apply);
    map.on('zoomend', apply);
    map.on('styledata', apply);
    return () => {
      map.off('moveend', apply);
      map.off('zoomend', apply);
      map.off('styledata', apply);
    };
  }, [reefExtentVisible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => applyBleachingState(map);

    if (map.isStyleLoaded()) apply();
    map.on('styledata', apply);
    return () => {
      map.off('styledata', apply);
    };
  }, [bleachingVisible, bleachingYear, bleachingObservationsGeoJson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyCaptionsVisibility(map);
  }, [captionsVisible]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const resetRaster = cortadVariableAppliedRef.current !== cortadVariable;
    cortadVariableAppliedRef.current = cortadVariable;
    if (map.isStyleLoaded()) applyCortadState(map, { resetRaster });
    const apply = () => applyCortadState(map);
    map.on('styledata', apply);
    return () => {
      map.off('styledata', apply);
    };
  }, [cortadVisible, cortadVariable, cortadMonth]);

  // Sync class colors/visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyBenthicStyling(map);
  }, [benthicClasses]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyReefExtentStyling(map);
  }, [reefExtentClasses]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    scheduleExpeditionTrackUpdate(map);

    const reapply = () => {
      applyExpeditionTrack(map);
    };
    map.on('styledata', reapply);
    map.on('idle', reapply);

    return () => {
      map.off('styledata', reapply);
      map.off('idle', reapply);
    };
  }, [expeditionTrackGeoJson, expeditionTrackVisible]);

  // Update colonies data and fit bounds.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const data = coloniesToGeoJson(colonies);
    // If style is mid-load (or was just rebuilt), source may not exist yet.
    if (!applyColoniesData(map)) {
      const onStyleData = () => {
        if (applyColoniesData(map)) {
          map.off('styledata', onStyleData);
        }
      };
      map.on('styledata', onStyleData);
    }

    if (expeditionTrackFitBounds) return;
    if (!colonyFitBounds || !data.features.length) return;

    const coords = data.features.map((f) => f.geometry.coordinates);
    let minX = coords[0][0];
    let minY = coords[0][1];
    let maxX = coords[0][0];
    let maxY = coords[0][1];
    for (const [x, y] of coords) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    map.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      { padding: 50, maxZoom: 14, duration: 500 },
    );
  }, [colonies, colonyFitBounds, expeditionTrackFitBounds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !expeditionTrackFitBounds) return;

    const bounds = getExpeditionTrackBounds(expeditionTrackGeoJson);
    if (!bounds) return;

    const fit = () => {
      map.fitBounds(bounds, { padding: 60, maxZoom: 8, duration: 500 });
    };

    if (!applyExpeditionTrackData(map, expeditionTrackGeoJson)) {
      const onStyleData = () => {
        if (applyExpeditionTrackData(map, expeditionTrackGeoJson)) {
          map.off('styledata', onStyleData);
          fit();
        }
      };
      map.on('styledata', onStyleData);
      return () => {
        map.off('styledata', onStyleData);
      };
    }

    fit();
    return undefined;
  }, [expeditionTrackGeoJson, expeditionTrackFitBounds]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusTarget || focusHandledKeyRef.current === focusTarget.key) return;

    const focus = () => {
      focusHandledKeyRef.current = focusTarget.key;
      clusterControllerRef.current?.clearSpider();
      map.flyTo({
        center: focusTarget.coordinates,
        zoom: focusTarget.zoom || 12,
        duration: 700,
        essential: true,
      });
      if (focusTarget.colony) {
        openColonyPopup(map, focusTarget.colony, focusTarget.coordinates);
      }
    };

    if (map.isStyleLoaded()) {
      focus();
      return;
    }

    map.once('load', focus);
    return () => {
      try { map.off('load', focus); } catch (_) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
}

